import { useEffect, useRef, useState } from "react";
import { api } from "./api";
import type { CellValue, Column, FileMeta, Item, StatusLabel, WorkspaceUser } from "./types";

// Workspace users are fetched once and shared across all people pickers.
let _usersPromise: Promise<WorkspaceUser[]> | null = null;
function loadUsers(): Promise<WorkspaceUser[]> {
  if (!_usersPromise) _usersPromise = api.users().then((r) => r.users).catch(() => []);
  return _usersPromise;
}

// Deterministic avatar color from a name.
export function avatarColor(name: string): string {
  const palette = ["#579bfc", "#00c875", "#fdab3d", "#e2445c", "#a25ddc", "#ff158a", "#784bd1", "#037f4c"];
  let h = 0;
  for (let i = 0; i < name.length; i++) h = (h * 31 + name.charCodeAt(i)) | 0;
  return palette[Math.abs(h) % palette.length];
}

function initials(name: string): string {
  return name.split(/\s+/).map((p) => p[0]).filter(Boolean).slice(0, 2).join("").toUpperCase();
}

export function resolveStatus(value: CellValue, col: Column): StatusLabel | null {
  if (!value || typeof value !== "object") return null;
  const labels = col.settings.labels ?? [];
  if ("id" in value) return labels.find((l) => l.id === (value as any).id) ?? null;
  if ("label" in value) {
    const match = labels.find((l) => l.label === (value as any).label);
    return match ?? { id: "", label: (value as any).label, color: (value as any).color || "#579bfc" };
  }
  return null;
}

function fmtMoney(n: number, unit = "€") {
  return `${n.toLocaleString("de-DE")} ${unit}`;
}
function fmtDate(d: string) {
  const dt = new Date(d);
  if (isNaN(+dt)) return d;
  return dt.toLocaleDateString("de-DE", { day: "2-digit", month: "short" });
}

interface CellProps {
  item: Item;
  col: Column;
  onChange: (value: CellValue) => void;
}

// Renders + inline-edits a single cell based on column type.
export function Cell({ item, col, onChange }: CellProps) {
  const value = item.cells[col.id] ?? null;

  switch (col.type) {
    case "status":
    case "label":
      return <StatusCell value={value} col={col} onChange={onChange} />;
    case "text":
    case "long_text":
      return <TextCell value={typeof value === "string" ? value : ""} onChange={onChange} />;
    case "numbers":
    case "money":
      return <MoneyCell value={value} unit={col.settings.unit || (col.type === "money" ? "€" : "")} onChange={onChange} />;
    case "date":
      return <DateCell value={value} onChange={onChange} />;
    case "timeline":
      return <TimelineCell value={value} onChange={onChange} />;
    case "people":
      return <PeopleCell value={value} onChange={onChange} />;
    case "link":
      return <LinkCell value={value} onChange={onChange} />;
    case "files":
      return <FilesCell value={value} itemId={item.id} colId={col.id} />;
    case "last_updated":
      return <div className="cell muted">{relTime(item.updated_at)}</div>;
    default:
      return <div className="cell" />;
  }
}

function TextCell({ value, onChange }: { value: string; onChange: (v: CellValue) => void }) {
  const [v, setV] = useState(value);
  useEffect(() => setV(value), [value]);
  return (
    <input className="cell-input" value={v}
      onChange={(e) => setV(e.target.value)}
      onBlur={() => v !== value && onChange(v)}
      onKeyDown={(e) => e.key === "Enter" && (e.target as HTMLInputElement).blur()} />
  );
}

function MoneyCell({ value, unit, onChange }: { value: CellValue; unit: string; onChange: (v: CellValue) => void }) {
  const num = value && typeof value === "object" && "number" in value ? value.number : null;
  const [editing, setEditing] = useState(false);
  const [v, setV] = useState(num?.toString() ?? "");
  useEffect(() => setV(num?.toString() ?? ""), [num]);
  if (editing)
    return <input className="cell-input" autoFocus type="number" value={v}
      onChange={(e) => setV(e.target.value)}
      onBlur={() => { setEditing(false); const n = parseFloat(v); onChange(isNaN(n) ? null : { number: n }); }} />;
  return <div className="cell money" onClick={() => setEditing(true)}>{num != null ? fmtMoney(num, unit) : ""}</div>;
}

function DateCell({ value, onChange }: { value: CellValue; onChange: (v: CellValue) => void }) {
  const d = value && typeof value === "object" && "date" in value ? value.date : "";
  const [editing, setEditing] = useState(false);
  if (editing)
    return <input className="cell-input" type="date" autoFocus defaultValue={d}
      onBlur={(e) => { setEditing(false); onChange(e.target.value ? { date: e.target.value } : null); }} />;
  return <div className="cell" onClick={() => setEditing(true)}>{d ? fmtDate(d) : ""}</div>;
}

function TimelineCell({ value, onChange }: { value: CellValue; onChange: (v: CellValue) => void }) {
  const tl = value && typeof value === "object" && "from" in value ? value : null;
  const [editing, setEditing] = useState(false);
  if (editing)
    return (
      <div className="cell" style={{ gap: 4 }}>
        <input className="input" style={{ padding: 2, fontSize: 11 }} type="date" defaultValue={(tl as any)?.from ?? ""}
          onChange={(e) => onChange({ from: e.target.value, to: (tl as any)?.to ?? e.target.value })} />
        <input className="input" style={{ padding: 2, fontSize: 11 }} type="date" defaultValue={(tl as any)?.to ?? ""}
          onBlur={(e) => { setEditing(false); onChange({ from: (tl as any)?.from ?? e.target.value, to: e.target.value }); }} />
      </div>
    );
  return (
    <div className="cell" onClick={() => setEditing(true)}>
      {tl ? <span className="timeline-bar">{fmtDate((tl as any).from)} – {fmtDate((tl as any).to)}</span> : ""}
    </div>
  );
}

// People picker: select actual workspace users (multi-select). Falls back to
// rendering legacy comma-separated names (from seed/Monday import).
function PeopleCell({ value, onChange }: { value: CellValue; onChange: (v: CellValue) => void }) {
  const selected = value && typeof value === "object" && "users" in value ? value.users : [];
  const legacy = value && typeof value === "object" && "names" in value ? value.names : [];
  const display = selected.length ? selected.map((u) => u.name || u.id) : legacy;

  const [open, setOpen] = useState(false);
  const [users, setUsers] = useState<WorkspaceUser[]>([]);
  const [q, setQ] = useState("");
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => { if (open) loadUsers().then(setUsers); }, [open]);
  useEffect(() => {
    if (!open) return;
    const h = (e: MouseEvent) => { if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false); };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, [open]);

  const selectedIds = new Set(selected.map((u) => u.id));
  function toggle(u: WorkspaceUser) {
    const next = selectedIds.has(u.id)
      ? selected.filter((x) => x.id !== u.id)
      : [...selected, { id: u.id, name: u.name || u.email }];
    onChange(next.length ? { users: next } : null);
  }
  const filtered = users.filter((u) =>
    (u.name + " " + u.email).toLowerCase().includes(q.toLowerCase()));

  return (
    <div ref={ref} style={{ position: "relative", width: "100%", height: "100%" }}>
      <div className="cell" onClick={() => setOpen((o) => !o)}>
        <div className="people-stack">
          {display.map((n, i) => (
            <span key={i} className="avatar" style={{ background: avatarColor(n) }} title={n}>{initials(n)}</span>
          ))}
          {!display.length && <span style={{ color: "var(--text-faint)" }}>+</span>}
        </div>
      </div>
      {open && (
        <div style={{
          position: "absolute", top: "100%", left: 0, zIndex: 25, background: "var(--surface)",
          border: "1px solid var(--border)", borderRadius: 8, padding: 6, minWidth: 220, boxShadow: "var(--shadow)",
        }}>
          <input className="input" autoFocus placeholder="Suchen…" value={q}
            onChange={(e) => setQ(e.target.value)} style={{ marginBottom: 6, padding: "6px 8px" }} />
          <div style={{ maxHeight: 220, overflow: "auto" }}>
            {filtered.map((u) => (
              <div key={u.id} className="row-flex" style={{ padding: "6px 6px", borderRadius: 5, cursor: "pointer" }}
                onClick={() => toggle(u)}>
                <input type="checkbox" readOnly checked={selectedIds.has(u.id)} />
                <span className="avatar" style={{ background: avatarColor(u.name || u.email), width: 22, height: 22 }}>{initials(u.name || u.email)}</span>
                <span style={{ flex: 1 }}>{u.name || u.email}</span>
              </div>
            ))}
            {!filtered.length && <div className="muted" style={{ padding: 8 }}>Keine User gefunden.</div>}
          </div>
        </div>
      )}
    </div>
  );
}

function LinkCell({ value, onChange }: { value: CellValue; onChange: (v: CellValue) => void }) {
  const link = value && typeof value === "object" && "url" in value ? value : null;
  const [editing, setEditing] = useState(false);
  if (editing)
    return <input className="cell-input" autoFocus defaultValue={(link as any)?.url ?? ""} placeholder="https://…"
      onBlur={(e) => { setEditing(false); const u = e.target.value.trim(); onChange(u ? { url: u, text: u.replace(/^https?:\/\//, "").slice(0, 24) } : null); }} />;
  return (
    <div className="cell" onClick={() => setEditing(true)}>
      {link ? <a href={(link as any).url} target="_blank" rel="noreferrer" onClick={(e) => e.stopPropagation()}>🔗 {(link as any).text}</a> : ""}
    </div>
  );
}

// Files cell: shows uploaded files as chips, supports click-to-upload and
// drag & drop of files (e.g. PDFs) directly onto the cell.
function FilesCell({ value, itemId, colId }: { value: CellValue; itemId: string; colId: string }) {
  const initial = value && typeof value === "object" && "files" in value ? (value.files as FileMeta[]) : [];
  const [files, setFiles] = useState<FileMeta[]>(initial);
  const [drag, setDrag] = useState(false);
  const [busy, setBusy] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  useEffect(() => { setFiles(initial); /* eslint-disable-next-line */ }, [JSON.stringify(initial)]);

  async function upload(list: FileList | File[]) {
    const arr = Array.from(list);
    if (!arr.length) return;
    setBusy(true);
    try {
      const r = await api.uploadFiles(itemId, colId, arr);
      setFiles((f) => [...f, ...r.files]);
    } catch { /* surfaced elsewhere */ }
    finally { setBusy(false); }
  }
  async function remove(id: string) {
    await api.deleteFile(id).catch(() => {});
    setFiles((f) => f.filter((x) => x.id !== id));
  }

  return (
    <div
      className="cell files-cell"
      style={drag ? { boxShadow: "inset 0 0 0 2px var(--accent)", background: "var(--surface)" } : undefined}
      onDragOver={(e) => { e.preventDefault(); setDrag(true); }}
      onDragLeave={() => setDrag(false)}
      onDrop={(e) => { e.preventDefault(); setDrag(false); upload(e.dataTransfer.files); }}
      title="Datei hierher ziehen oder klicken zum Hochladen"
    >
      <input ref={inputRef} type="file" multiple hidden accept="application/pdf,image/*"
        onChange={(e) => e.target.files && upload(e.target.files)} />
      {files.map((f) => (
        <span key={f.id} className="file-chip" style={{ display: "inline-flex", alignItems: "center", gap: 4 }}>
          <a href={api.fileUrl(f.id)} target="_blank" rel="noreferrer" onClick={(e) => e.stopPropagation()} title={f.name}>
            📎 {f.name.length > 12 ? f.name.slice(0, 10) + "…" : f.name}
          </a>
          <span style={{ cursor: "pointer", color: "var(--text-faint)" }}
            onClick={(e) => { e.stopPropagation(); remove(f.id); }}>×</span>
        </span>
      ))}
      <span className="file-add" onClick={() => inputRef.current?.click()} style={{ color: "var(--text-faint)", cursor: "pointer" }}>
        {busy ? "…" : files.length ? "+" : "+ Datei"}
      </span>
    </div>
  );
}

function StatusCell({ value, col, onChange }: { value: CellValue; col: Column; onChange: (v: CellValue) => void }) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const current = resolveStatus(value, col);
  const labels = col.settings.labels ?? [];

  useEffect(() => {
    if (!open) return;
    const h = (e: MouseEvent) => { if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false); };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, [open]);

  return (
    <div ref={ref} style={{ position: "relative", width: "100%", height: "100%" }}>
      <div className={"pill" + (current ? "" : " empty")} style={current ? { background: current.color } : undefined}
        onClick={() => setOpen((o) => !o)}>
        {current?.label ?? ""}
      </div>
      {open && (
        <div style={{
          position: "absolute", top: "100%", left: 0, zIndex: 20, background: "var(--surface)",
          border: "1px solid var(--border)", borderRadius: 8, padding: 6, minWidth: 160, boxShadow: "var(--shadow)",
        }}>
          {labels.map((l) => (
            <div key={l.id} className="pill" style={{ background: l.color, height: 26, borderRadius: 5, marginBottom: 4, cursor: "pointer" }}
              onClick={() => { onChange({ id: l.id }); setOpen(false); }}>{l.label}</div>
          ))}
          <div className="pill empty" style={{ height: 26, borderRadius: 5, cursor: "pointer" }}
            onClick={() => { onChange(null); setOpen(false); }}>– leer –</div>
        </div>
      )}
    </div>
  );
}

function relTime(ts: number): string {
  const diff = Date.now() - ts;
  const min = Math.floor(diff / 60000);
  if (min < 1) return "gerade eben";
  if (min < 60) return `vor ${min} Min.`;
  const h = Math.floor(min / 60);
  if (h < 24) return `vor ${h} Std.`;
  const d = Math.floor(h / 24);
  if (d < 30) return `vor ${d} Tg.`;
  return new Date(ts).toLocaleDateString("de-DE");
}
